import React, { useEffect, useState } from 'react';
import { FaCog, FaSlidersH, FaUmbrellaBeach, FaStar, FaMoon } from 'react-icons/fa';
import EkBayramYonetici from './EkBayramYonetici';
import PresetYonetici from './PresetYonetici';
import AdliTatilSecici from './AdliTatilSecici';
import SureSecimi from './SureSecimi';
import { REJIMLER } from './adliTatil';
import { BIRIMLER, VARSAYILAN_SURE, VARSAYILAN_BIRIM, VARSAYILAN_ADLI_TATIL } from './ayarlar';

/** Dokunmaya uygun açma/kapama satırı (emerald aktif). */
function Toggle({ checked, onChange, children }) {
  return (
    <label className="-mx-1 flex cursor-pointer select-none items-center gap-3 rounded-lg px-1 py-2 active:bg-slate-50">
      <span className={`relative inline-flex h-6 w-11 flex-none rounded-full transition-colors ${checked ? 'bg-emerald-600' : 'bg-slate-300'}`}>
        <span className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-5' : ''}`} />
        <input type="checkbox" className="sr-only" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      </span>
      <span className="text-sm leading-snug text-slate-600">{children}</span>
    </label>
  );
}

/** Sekme içi küçük bölüm başlığı. */
function AltBaslik({ children }) {
  return <div className="mb-2.5 text-xs font-semibold uppercase tracking-wide text-slate-400">{children}</div>;
}

const SEKMELER = [
  { id: 'genel', label: 'Genel', Icon: FaSlidersH },
  { id: 'adli', label: 'Adli Tatil', Icon: FaUmbrellaBeach },
  { id: 'sureler', label: 'Süreler', Icon: FaStar },
  { id: 'bayram', label: 'Bayramlar', Icon: FaMoon },
];

/**
 * Ayarlar — mobilde alttan açılan sayfa (bottom sheet), masaüstünde ortalı diyalog.
 * Sekmeli yapı: tek seferde tek bölüm görünür (uzun kaydırma yok). Yapışkan başlık +
 * sekme çubuğu + kaydırılabilir gövde + yapışkan footer. Süre/davranış/rejim ayarları
 * "Kaydet" ile uygulanıp pencere kapanır; preset/bayram/favori anında kaydedilir.
 */
function KanunYoluAyarlarModal({ isOpen, onClose, ayar, onSave, onEkBayramEkle, onEkBayramSil, onPresetEkle, onPresetSil, onFavoriTutle }) {
  const [draft, setDraft] = useState(ayar);
  const [sekme, setSekme] = useState('genel');

  // Taslağı/sekmeyi YALNIZCA modal açılırken sıfırla. (ayar'ı bağımlılığa koymuyoruz:
  // favori/preset/bayram anlık kaydedince ayar değişir; o anda sekme sıfırlanmamalı.)
  useEffect(() => {
    if (isOpen) { setDraft(ayar); setSekme('genel'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Açıkken arka plan kaymasını kilitle + Escape ile kapat.
  useEffect(() => {
    if (!isOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const kaydet = () => {
    const s = Number(draft.defaultSure);
    onSave({
      defaultSure: (s >= 1 && s <= 999) ? Math.floor(s) : VARSAYILAN_SURE,
      defaultBirim: BIRIMLER.includes(draft.defaultBirim) ? draft.defaultBirim : VARSAYILAN_BIRIM,
      showSure: draft.showSure === true,
      autoHesap: draft.autoHesap !== false,
      silinceSifirla: draft.silinceSifirla !== false,
      adliTatil: REJIMLER.includes(draft.adliTatil) ? draft.adliTatil : VARSAYILAN_ADLI_TATIL,
      hazirSureGoster: draft.hazirSureGoster === true,
    });
    onClose(); // Modern yaklaşım: kaydet → uygula + pencereyi kapat.
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4">
      <div className="absolute inset-0 bg-slate-900/55 backdrop-blur-sm" onClick={onClose} />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="Ayarlar"
        className="kys-sheet relative flex max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-2xl border border-slate-200 bg-white shadow-2xl sm:max-h-[88vh] sm:max-w-lg sm:rounded-2xl"
      >
        {/* Mobil tutmaç */}
        <div className="flex justify-center pt-2.5 sm:hidden">
          <span className="h-1.5 w-10 rounded-full bg-slate-300" />
        </div>

        {/* Yapışkan başlık */}
        <div className="flex flex-none items-center justify-between px-5 pb-3 pt-3 sm:pt-4">
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-100 text-amber-600">
              <FaCog className="h-3.5 w-3.5" />
            </span>
            <h2 className="font-display text-lg font-semibold text-slate-900">Ayarlar</h2>
          </div>
          <button type="button" onClick={onClose} className="app-modal-close" aria-label="Kapat">&times;</button>
        </div>

        {/* Sekme çubuğu */}
        <div role="tablist" aria-label="Ayar bölümleri" className="scrollbar-hidden flex flex-none gap-1 overflow-x-auto border-b border-slate-100 px-3">
          {SEKMELER.map((s) => {
            const aktif = sekme === s.id;
            return (
              <button
                key={s.id}
                type="button"
                role="tab"
                onClick={() => setSekme(s.id)}
                aria-selected={aktif}
                className={`relative flex flex-none items-center gap-1.5 whitespace-nowrap px-3 py-2.5 text-sm font-medium transition-colors ${
                  aktif ? 'text-slate-900' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                <s.Icon className="h-3.5 w-3.5" />
                {s.label}
                {aktif ? <span className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-amber-400" /> : null}
              </button>
            );
          })}
        </div>

        {/* Kaydırılabilir gövde (aktif sekme) */}
        <div className="scrollbar-slim min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4">
          {sekme === 'genel' ? (
            <div className="space-y-5">
              <div>
                <AltBaslik>Kanun yolu süresi</AltBaslik>
                <label className="app-label">Varsayılan süre</label>
                <div className="max-w-[260px]">
                  <SureSecimi
                    sure={draft.defaultSure}
                    birim={draft.defaultBirim}
                    onSureChange={(v) => set('defaultSure', v)}
                    onBirimChange={(v) => set('defaultBirim', v)}
                  />
                </div>
                <Toggle checked={draft.showSure === true} onChange={(v) => set('showSure', v)}>
                  Süre alanını ana ekranda göster
                </Toggle>
              </div>

              <div className="border-t border-slate-100 pt-4">
                <AltBaslik>Tarih girişi</AltBaslik>
                <Toggle checked={draft.autoHesap !== false} onChange={(v) => set('autoHesap', v)}>
                  Tam tarih girilince otomatik hesapla
                </Toggle>
                <Toggle checked={draft.silinceSifirla !== false} onChange={(v) => set('silinceSifirla', v)}>
                  Tarih silinince hesaplamayı sıfırla
                </Toggle>
              </div>
            </div>
          ) : null}

          {sekme === 'adli' ? (
            <div>
              <label className="app-label">Varsayılan rejim</label>
              <AdliTatilSecici value={draft.adliTatil || 'kapali'} onChange={(v) => set('adliTatil', v)} />
              <p className="mt-3 text-xs leading-relaxed text-slate-500">
                Adli tatil <strong className="text-slate-700">20 Temmuz – 31 Ağustos</strong> arasıdır. Süre bu döneme
                denk gelirse uzar: <strong className="text-slate-700">HMK/İYUK +1 hafta</strong>,{' '}
                <strong className="text-slate-700">CMK +3 gün</strong>. İdari yargı süreleri (İYUK m.8/3) HMK rejimini
                kullanır. Ana ekrandan her hesap için değiştirilebilir; hazır süre seçilince o sürenin rejimi uygulanır.
              </p>
            </div>
          ) : null}

          {sekme === 'sureler' ? (
            <div>
              <Toggle checked={draft.hazirSureGoster === true} onChange={(v) => set('hazirSureGoster', v)}>
                Hazır süreleri ana ekranda göster
              </Toggle>
              <div className="mt-3">
                <PresetYonetici
                  kullaniciPresetleri={ayar.presetler}
                  favoriler={ayar.favoriler}
                  onEkle={onPresetEkle}
                  onSil={onPresetSil}
                  onFavoriTutle={onFavoriTutle}
                />
              </div>
            </div>
          ) : null}

          {sekme === 'bayram' ? (
            <EkBayramYonetici
              ekBayramlar={ayar.ekBayramlar}
              onEkle={onEkBayramEkle}
              onSil={onEkBayramSil}
            />
          ) : null}
        </div>

        {/* Yapışkan alt çubuk */}
        <div className="flex flex-none items-center justify-end gap-3 border-t border-slate-100 bg-white px-5 py-3.5 pb-[max(0.875rem,env(safe-area-inset-bottom))]">
          <button type="button" className="btn-secondary !px-5 !py-2.5" onClick={onClose}>
            Vazgeç
          </button>
          <button type="button" className="btn-success !px-5 !py-2.5" onClick={kaydet}>
            Kaydet
          </button>
        </div>
      </div>
    </div>
  );
}

export default KanunYoluAyarlarModal;
